import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Minimal accessible modal dialog.
 *
 * - Renders via portal to <body> so it escapes any positioned ancestor.
 * - Closes on ESC + backdrop click (unless `dismissible={false}`).
 * - Locks body scroll while open.
 * - Initial focus is the first focusable child of `children` (relies on the
 *   caller putting an autoFocus or just the natural tab order).
 *
 * Visual: glass panel (matching DESIGN.md surfaces) over a heavy blurred
 * backdrop. The panel itself is `surface-glass-strong` with the brand's
 * primary-glow + ghost border, so dialogs feel native to the rest of the
 * app rather than a generic shadcn modal.
 *
 * For a richer system (focus trap, animation primitives) we'd swap to Radix.
 */
export function Dialog({
  open,
  onClose,
  children,
  labelledBy,
  dismissible = true,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  labelledBy?: string;
  dismissible?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, dismissible]);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        onClick={dismissible ? onClose : undefined}
        aria-hidden
      />
      <div
        className="glass-strong relative w-full max-w-md rounded-2xl p-6"
        style={{
          boxShadow:
            '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 24px 48px -16px rgba(0,0,0,0.6), 0 0 64px -16px rgba(124,77,255,0.35)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
